import { useTranslation } from 'react-i18next';
import type { PingDeposit } from '../services/api';
import { bankgiroCodeLine, BANKGIRO_SLIP_WIDTH_MM, BANKGIRO_SLIP_HEIGHT_MM, BANKGIRO_CELL_MM, BANKGIRO_CODE_BASELINE_MM } from '../services/bankgiroSlip';

export function classicSlipAmount(deposit: PingDeposit): number | null {
  if (!['awaiting_payment', 'underfunded'].includes(deposit.status)) return null;
  const remaining = deposit.amountOre - (deposit.receivedOre ?? 0);
  return Number.isSafeInteger(remaining) && remaining > 0 ? remaining : null;
}

export function classicSlipCodeLine(deposit: PingDeposit): string | null {
  const amount = classicSlipAmount(deposit);
  if (deposit.mode === 'mock' || deposit.currency !== 'SEK' || amount === null) return null;
  return bankgiroCodeLine(deposit.notice?.reference ?? '', deposit.notice?.bankAccount.bankgiro_number ?? '', amount);
}

/** Fixed bankgiro form geometry: never reflow or condense its machine-readable line. */
export function PingClassicSlip({ deposit }: { deposit: PingDeposit }) {
  const { t } = useTranslation();
  const line = classicSlipCodeLine(deposit);
  if (!line) return null;
  const amount = classicSlipAmount(deposit)!;
  // A Swedish banking form keeps its field labels in Swedish; surrounding UI is localized.
  const label = (key: string) => t(`pingDeposit.classic.form.${key}`, { lng: 'sv' });

  return <article className="ping-classic-slip" aria-label={t('pingDeposit.classic.title')}>
    <svg className="ping-bankgiro-form" viewBox={`0 0 ${BANKGIRO_SLIP_WIDTH_MM} ${BANKGIRO_SLIP_HEIGHT_MM}`} role="img" aria-label={t('pingDeposit.classic.title')} lang="sv">
      <rect className="ping-bankgiro-paper" width={BANKGIRO_SLIP_WIDTH_MM} height={BANKGIRO_SLIP_HEIGHT_MM} />
      <g className="ping-bankgiro-rules">
        <path d="M0 6H210.82 M133 6V15H210.82 M166 6V15 M112 59H210.82 M112 59V68 M140 59V68 M0 68H210.82 M0 71.5H210.82 M81 71.5V76 M104 71.5V76 M114 71.5V76" />
      </g>
      <g className="ping-bankgiro-labels">
        <text x="20" y="4.7" className="ping-bankgiro-brand">{label('brand')}</text>
        <text x="111" y="5" className="ping-bankgiro-heading">{label('heading')}</text>
        <text x="134" y="8.8" className="ping-bankgiro-small">{label('bankFee')}</text>
        <text x="176" y="14" className="ping-bankgiro-ocr">{label('ocr')}</text>
        <text x="20" y="11">{label('instruction1')}</text>
        <text x="20" y="14.5">{label('instruction2')}</text>
        <text x="20" y="36" className="ping-bankgiro-small">{label('payer')}</text>
        <text x="113" y="62" className="ping-bankgiro-small">{label('bankgiro')}</text>
        <text x="141" y="62" className="ping-bankgiro-small">{label('recipient')}</text>
        <text x="14" y="70.6" className="ping-bankgiro-warning">{label('noChanges')}</text>
        <text x="78" y="70.6" className="ping-bankgiro-warning">{label('noMessage')}</text>
        <text x="164" y="70.6" className="ping-bankgiro-warning">{label('machineRead')}</text>
        <text x="20" y="75" className="ping-bankgiro-small">{label('reference')}</text>
        <text x="82" y="75" className="ping-bankgiro-small">{label('kronor')}</text>
        <text x="105" y="75" className="ping-bankgiro-small">{label('ore')}</text>
      </g>
      <g className="ping-bankgiro-data">
        <text x="20" y="23" className="ping-bankgiro-test">{label('testNotice')}</text>
        <text x="20" y="28">{label('purpose')}</text>
        {deposit.status === 'underfunded' && <text x="20" y="52">{label('remaining')}</text>}
        <text x="113" y="66.7" data-testid="classic-bankgiro">{deposit.notice!.bankAccount.bankgiro_number}</text>
        <text x="141" y="66.7">{label('recipientByNumber')}</text>
      </g>
      <text className="ping-bankgiro-code" y={BANKGIRO_CODE_BASELINE_MM} xmlSpace="preserve" data-testid="classic-code-line">
        {[...line].map((character, index) => <tspan key={index} x={(index + 0.5) * BANKGIRO_CELL_MM}>{character}</tspan>)}
      </text>
    </svg>
    <dl className="visually-hidden">
      <dt>{t('pingDeposit.reference')}</dt><dd data-testid="classic-reference">{deposit.notice!.reference}</dd>
      <dt>{t('pingDeposit.amount')}</dt><dd data-testid="classic-amount">{new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false }).format(amount / 100)}</dd>
    </dl>
  </article>;
}
